import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useContext } from 'react'

import Auftrag25_10Form from './Auftrag25_10Form'
import Auftrag25_30Form from './Auftrag25_30Form'
import { useBefehlForm } from '../../../hooks'
import { BefehlFormProps, BefehlFormWrapper } from '../Generic'
import { Befehl25 } from '../../../models'
import { Befehl25FormData, createBefehl25FormValidationSchema } from './schema'
import {
  convertBefehl25FormDataToModel,
  convertModelToBefehl25FormData
} from './converters'
import Auftrag25_20Form from './Auftrag25_20Form'
import MarkAuftraegeAsDoneContext from '../../../contexts/MarkAuftraegeAsDoneContext'
import { findAuftragByKennung } from '../converter-utils'

/**
 * Main form component for Befehl 25
 */

export default function Befehl25Form({
  befehl,
  onSubmit,
  readOnly = false,
  'data-testid': testId = 'befehl-25-form',
  activateEmptyFieldWarnings = false,
  displayHeaderOnly = false
}: Readonly<BefehlFormProps<Befehl25>>) {
  const { t } = useTranslation('ui-befehle')

  const formMethods = useBefehlForm<
    ReturnType<typeof createBefehl25FormValidationSchema>
  >({
    readOnly,
    activateEmptyFieldWarnings,
    schema: createBefehl25FormValidationSchema(),
    initialData: befehl ? convertModelToBefehl25FormData(befehl) : undefined
  })
  const { watch, setValue } = formMethods
  const auftrag_25_10_enabled = watch('auftrag_25_10.enabled')

  useEffect(() => {
    if (!auftrag_25_10_enabled) {
      setValue('auftrag_25_20.enabled', false)
    }
  }, [auftrag_25_10_enabled, setValue])

  const handleSubmit = useCallback(
    (data: Befehl25FormData) => {
      const updatedBefehl = convertBefehl25FormDataToModel(
        data,
        befehl.frontendId
      )
      onSubmit(updatedBefehl)
    },
    [befehl.frontendId, onSubmit]
  )

  const context = useContext(MarkAuftraegeAsDoneContext)
  const displaySlideToUnlock =
    (context?.isBefehlsnachrichtQuittiert &&
      (befehl.auftraege?.length ?? 0) >= 2) ||
    false

  const displaySlideToUnlock25_10 =
    displaySlideToUnlock && !!findAuftragByKennung(befehl.auftraege, '25.10')

  const displaySlideToUnlock25_20 =
    displaySlideToUnlock && !!findAuftragByKennung(befehl.auftraege, '25.20')

  const displaySlideToUnlock25_30 =
    displaySlideToUnlock && !!findAuftragByKennung(befehl.auftraege, '25.30')

  return (
    <BefehlFormWrapper<Befehl25FormData>
      befehlKennung="25"
      title={t('befehle.25.description')}
      formMethods={formMethods}
      onSubmit={handleSubmit}
      readOnly={readOnly}
      data-testid={`${testId}-wrapper`}
      activateEmptyFieldWarnings={activateEmptyFieldWarnings}
      displayHeaderOnly={displayHeaderOnly}
    >
      {/* Auftrag 25.10 */}
      <Auftrag25_10Form
        befehlKennung="25"
        fieldPrefix="auftrag_25_10"
        readOnly={readOnly}
        displaySlideToUnlock={displaySlideToUnlock25_10}
        data-testid={`${testId}-auftrag-25-10`}
        activateEmptyFieldWarnings={activateEmptyFieldWarnings}
      />
      {auftrag_25_10_enabled && (
        <Auftrag25_20Form
          fieldPrefix="auftrag_25_20"
          befehlKennung="25"
          readOnly={readOnly}
          displaySlideToUnlock={displaySlideToUnlock25_20}
          data-testid={`${testId}-auftrag-25-20`}
          activateEmptyFieldWarnings={activateEmptyFieldWarnings}
          disabled={readOnly}
          noIndent={true}
        />
      )}

      {/* Auftrag 25.30 */}
      <Auftrag25_30Form
        befehlKennung="25"
        fieldPrefix="auftrag_25_30"
        readOnly={readOnly}
        displaySlideToUnlock={displaySlideToUnlock25_30}
        data-testid={`${testId}-auftrag-25-30`}
        activateEmptyFieldWarnings={activateEmptyFieldWarnings}
      />
    </BefehlFormWrapper>
  )
}
